import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import SkillReviewItem from './SkillReviewItem';

function SkillsReviewList({ skills, isLoading, onApprove, onDecline }) {
  const [expandedSkills, setExpandedSkills] = useState([]);

  const toggleSkill = (itemId) => {
    setExpandedSkills((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId],
    );
  };

  const preparedSkills = useMemo(() => {
    return skills.map((skill) => {
      const uniqueDetails = {};

      skill.details.forEach((detail) => {
        const { name, status, comment, recommendation, itemId } = detail;

        if (!uniqueDetails[name]) {
          uniqueDetails[name] = {
            itemId,
            name,
            status,
            comment: comment || '',
            recommendation: recommendation || '',
            successCriteria: [detail.successCriteria],
            materials: [
              {
                name: detail.materialName,
                type: detail.materialType,
                link: detail.link,
              },
            ],
          };
        } else {
          uniqueDetails[name].successCriteria = [
            ...new Set([...uniqueDetails[name].successCriteria, detail.successCriteria]),
          ];
          uniqueDetails[name].materials.push({
            name: detail.materialName,
            type: detail.materialType,
            link: detail.link,
          });
          if (status) {
            uniqueDetails[name].status = status;
          }
          if (recommendation) {
            uniqueDetails[name].recommendation = recommendation;
          }
        }
      });

      return {
        ...skill,
        preparedDetails: Object.values(uniqueDetails),
      };
    });
  }, [skills]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="p-4 rounded-lg border animate-pulse bg-muted/30">
            <div className="h-5 w-48 bg-muted rounded mb-3" />
            <div className="space-y-2">
              <div className="h-4 w-full bg-muted rounded" />
              <div className="h-4 w-3/4 bg-muted rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (skills.length === 0) {
    return <div className="text-center py-8 text-muted-foreground">No skills to review</div>;
  }

  return (
    <div className="space-y-3">
      {preparedSkills.map((skill) => (
        <SkillReviewItem
          key={skill.itemId}
          skill={skill}
          isExpanded={expandedSkills.includes(skill.itemId)}
          onToggle={() => toggleSkill(skill.itemId)}
          onApprove={onApprove}
          onDecline={onDecline}
        />
      ))}
    </div>
  );
}

SkillsReviewList.propTypes = {
  skills: PropTypes.arrayOf(
    PropTypes.shape({
      itemId: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      details: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    }),
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  onApprove: PropTypes.func.isRequired,
  onDecline: PropTypes.func.isRequired,
};

export default SkillsReviewList;
